import React, { useState, useEffect, useRef } from "react";

const colors = {
  mainBlue: "#061941",
  cafeNoir: "#583E23",
  carrieban: "#3C6E71",
  isabelline: "#F4F3EE",
  gold: "#F2AF29"
};

const termsSections = [
  {
    id: 1,
    title: "Eligibility",
    content: "You must be at least 18 years old (or the age of majority in your jurisdiction) to use our Website. If you are under 18, you may only use the Website with parental or guardian consent."
  },
  {
    id: 2,
    title: "Use of Website",
    content: "You agree to use our Website only for lawful purposes. You may not engage in unauthorized access, distribution of malware, disruptive behavior, or copy/reproduce content without permission."
  },
  {
    id: 3,
    title: "User Accounts",
    content: "Certain features may require account registration. You are responsible for safeguarding login credentials. Notify us immediately if you suspect unauthorized use of your account."
  },
  {
    id: 4,
    title: "Payments & Subscriptions",
    content: "Fees must be paid via approved payment methods. Subscriptions renew automatically unless canceled prior to renewal. Refund policies are outlined separately."
  },
  {
    id: 5,
    title: "Intellectual Property",
    content: "All Website content, including text, graphics, logos, and software, is owned or licensed by NeuHawk. You may not use trademarks, branding, or content without written consent."
  },
  {
    id: 6,
    title: "Third-Party Services",
    content: "Our Website may include links to third-party services. We are not responsible for their availability, accuracy, or practices."
  },
  {
    id: 7,
    title: "Disclaimers",
    content: "The Website is provided \"as is\" and \"as available.\" We do not guarantee uninterrupted access, accuracy, or error-free operation."
  },
  {
    id: 8,
    title: "Limitation of Liability",
    content: "To the fullest extent permitted by law, we are not liable for indirect or incidental damages, including loss of profits or data."
  },
  {
    id: 9,
    title: "Indemnification",
    content: "You agree to indemnify and hold harmless Neuhawk, affiliates, and employees from claims or damages arising from your use of the Website."
  },
  {
    id: 10,
    title: "Termination",
    content: "We may suspend or terminate your access to the Website at our discretion if you violate these Terms."
  },
  {
    id: 11,
    title: "Governing Law",
    content: "These Terms are governed by the laws of Delaware, without regard to conflicts of law."
  },
  {
    id: 12,
    title: "Changes to Terms",
    content: "We may update these Terms from time to time. Continued use of the Website constitutes acceptance."
  },
  {
    id: 13,
    title: "Contact Us",
    content: "📧 Email: Neuhawkai@gmail.com"
  }
];

export default function Terms() {
  const [visibleSections, setVisibleSections] = useState([]);
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = parseInt(entry.target.dataset.sectionId);
            setVisibleSections(prev => 
              prev.includes(sectionId) ? prev : [...prev, sectionId]
            );
          }
        });
      },
      { threshold: 0.1, rootMargin: "50px" }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: `linear-gradient(120deg, ${colors.mainBlue} 70%, ${colors.gold} 95%, ${colors.gold} 100%)`,
        fontFamily: "Segoe UI, Arial, sans-serif",
        paddingBottom: "7rem"
      }}
    >
      {/* Header */}
      <header
        style={{
          padding: "2.3rem 1rem 1.7rem 1rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "1.2rem"
        }}
      >
        <h1
          style={{
            fontFamily: "Poppins, Segoe UI, Arial, sans-serif",
            fontSize: "2.4rem",
            fontWeight: 800,
            letterSpacing: "2px",
            color: colors.isabelline,
            margin: 0,
            lineHeight: 1.1
          }}
        >
          Terms & Conditions
        </h1>
      </header>

      {/* Content Container */}
      <div
        style={{
          maxWidth: "1000px",
          margin: "0 auto",
          padding: "0 1.5rem"
        }}
      >
        {/* Date Info */}
        <div
          style={{
            background: colors.isabelline,
            borderRadius: "20px",
            padding: "2rem",
            marginBottom: "2.5rem",
            boxShadow: "0 8px 32px 0 rgba(88, 62, 35, 0.12)",
            border: `3px solid ${colors.gold}`,
            position: "relative",
            overflow: "hidden"
          }}
        >
          {/* Unique diagonal accent */}
          <div
            style={{
              position: "absolute",
              top: "-20px",
              right: "-20px",
              width: "80px",
              height: "80px",
              background: colors.gold,
              transform: "rotate(45deg)",
              opacity: 0.1
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "0",
              left: 0,
              right: 0,
              height: "5px",
              background: `linear-gradient(90deg, ${colors.gold}, ${colors.carrieban})`
            }}
          />
          <p style={{ margin: "0 0 0.5rem 0", color: colors.mainBlue, fontWeight: 600, fontSize: "1.1rem" }}>
            <strong>Effective Date:</strong> 08/17/2025
          </p>
          <p style={{ margin: 0, color: colors.mainBlue, fontWeight: 600, fontSize: "1.1rem" }}>
            <strong>Last Updated:</strong> 08/17/2025
          </p>
        </div>

        {/* Terms Sections */}
        <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          {termsSections.map((section, index) => (
            <div
              key={section.id}
              ref={el => sectionRefs.current[index] = el}
              data-section-id={section.id}
              style={{
                background: colors.isabelline,
                borderRadius: "20px",
                padding: "0",
                boxShadow: "0 8px 32px 0 rgba(88, 62, 35, 0.12)",
                border: `3px solid ${colors.gold}`,
                position: "relative",
                overflow: "hidden",
                opacity: visibleSections.includes(section.id) ? 1 : 0,
                transform: visibleSections.includes(section.id) 
                  ? "translateX(0) scale(1)" 
                  : "translateX(-50px) scale(0.95)",
                transition: "all 0.7s cubic-bezier(0.61, 0.09, 0.66, 1.2)",
                transitionDelay: `${index * 0.08}s`
              }}
            >
              {/* Unique corner accent */}
              <div
                style={{
                  position: "absolute",
                  top: "-15px",
                  right: "-15px",
                  width: "60px",
                  height: "60px",
                  background: colors.gold,
                  transform: "rotate(45deg)",
                  opacity: 0.08
                }}
              />
              
              {/* Top accent line */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: `linear-gradient(90deg, ${colors.gold}, ${colors.carrieban})`
                }}
              />

              {/* Side-by-side layout */}
              <div style={{ display: "flex", alignItems: "stretch" }}>
                {/* Left side - Number */}
                <div
                  style={{
                    background: `linear-gradient(135deg, ${colors.gold}, ${colors.carrieban})`,
                    color: colors.isabelline,
                    padding: "2rem 1.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "120px",
                    position: "relative"
                  }}
                >
                  <div
                    style={{
                      fontSize: "2.5rem",
                      fontWeight: 800,
                      fontFamily: "Poppins, Segoe UI, Arial, sans-serif",
                      textShadow: "0 2px 4px rgba(0,0,0,0.2)"
                    }}
                  >
                    {section.id}
                  </div>
                  {/* Subtle pattern overlay */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)",
                      pointerEvents: "none"
                    }}
                  />
                </div>

                {/* Right side - Content */}
                <div style={{ padding: "2rem", flex: 1 }}>
                  <h2 style={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    color: colors.mainBlue,
                    margin: "0 0 1rem 0",
                    fontFamily: "Poppins, Segoe UI, Arial, sans-serif"
                  }}>
                    {section.title}
                  </h2>
                  <p style={{
                    fontSize: "1rem",
                    color: colors.mainBlue,
                    lineHeight: 1.7,
                    margin: 0,
                    opacity: 0.9
                  }}>
                    {section.content}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
